<template>
  <div>
    <b-skeleton :loading="loadingInvoice" height="100px" v-if="loadingInvoice" />
    <invoice-form
      v-else
      :form-data.sync="formData"
      :loading="isLoading"
      @submit="onSubmit"
      @cancel="onCancel"
    />
  </div>
</template>

<script>
import Logo from '@core/layouts/components/Logo.vue';
import { ref, onUnmounted, watch, onMounted, computed } from '@vue/composition-api';
import Ripple from 'vue-ripple-directive';
import store from '@/store';
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BButton,
  BCardText,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BFormTextarea,
  BFormCheckbox,
  BPopover,
  VBToggle,
  BSkeleton,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import flatPickr from 'vue-flatpickr-component';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { useToast } from 'vue-toastification/composition';
import moment from 'moment';

import InvoiceForm from '../invoice-form/InvoiceForm.vue';
import invoiceStoreModule from '../invoiceStoreModule';
import router from '@/router';
import ItemsStoreModule from '../../items/ItemsStoreModule';

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BButton,
    BCardText,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BFormTextarea,
    BFormCheckbox,
    BPopover,
    flatPickr,
    vSelect,
    Logo,
    InvoiceForm,
    ToastificationContent,
    BSkeleton,
  },
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  setup() {
    const id = router.currentRoute.params.id;
    const toast = useToast();

    // Invoice Store Module
    const INVOICE_APP_STORE_MODULE_NAME = 'sales-invoice';

    // Register module
    if (!store.hasModule(INVOICE_APP_STORE_MODULE_NAME))
      store.registerModule(INVOICE_APP_STORE_MODULE_NAME, invoiceStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(INVOICE_APP_STORE_MODULE_NAME))
        store.unregisterModule(INVOICE_APP_STORE_MODULE_NAME);
    });

    // Items Store Module
    const ITEMS_APP_STORE_MODULE_NAME = 'items';
    if (!store.hasModule(ITEMS_APP_STORE_MODULE_NAME))
      store.registerModule(ITEMS_APP_STORE_MODULE_NAME, ItemsStoreModule);
    onUnmounted(() => {
      if (store.hasModule(ITEMS_APP_STORE_MODULE_NAME))
        store.unregisterModule(ITEMS_APP_STORE_MODULE_NAME);
    });

    const businessID = computed(() => store.state.user?.businessID);

    const loadingInvoice = ref(true);
    const isLoading = ref(false);
    const formData = ref(null);

    store
      .dispatch('sales-invoice/fetchInvoice', { id: router.currentRoute.params.id })
      .then((invoiceResponse) => {
        const itemIds = invoiceResponse.details.map((detail) => detail.itemId);
        store
          .dispatch('items/fetchItemsByIds', itemIds)
          .then((itemsResponse) => {
            const { entity, details } = invoiceResponse;

            formData.value = {
              entity: {
                id: entity.id,
                businessID: businessID.value,
                customerID: entity.customerID,
                customerCode: entity.customerCode,
                docNo: entity.docNo,
                tranDate: moment(entity.tranDate).format('YYYY-MM-DD'),
                salesTypeID: entity.salesTypeID,
                salesTaxInvoiceNo: entity.salesTaxInvoiceNo,
                sroScheduleNo: entity.sroScheduleNo,
                sroItemSerialNo: entity.sroItemSerialNo,
                remarks: entity.remarks,
                fbrInvoiceNo: entity.fbrInvoiceNo,
                grossAmount: entity.grossAmount,
                totalSalesTax: entity.totalSalesTax,
                notifiedValue: entity.notifiedValue,
                totalFurtherTax: entity.totalFurtherTax,
                totalAdvanceTax: entity.totalAdvanceTax,
                totalDiscount: entity.totalDiscount,
                netAmount: entity.netAmount,
                totalTax: entity.totalTax,
                valWithoutTax: entity.valWithoutTax,
                tax236GValue: entity.tax236GValue,
                valIncludingTax: entity.valIncludingTax,
              },
              details: details.map((detail) => {
                const item = itemsResponse.find((i) => Number(i.id) === Number(detail.itemId || detail.itemID)) || {};

                return {
                  itemId: detail.itemId || detail.itemID,
                  itemHSCode: detail.itemHSCode || item.meta_HSCode || '',
                  qty: detail.qty,
                  unit: item.meta_unit || detail.unit || '',
                  uom: item.unitID || detail.uom || '',
                  rate: detail.rate || item.tradePrice || 0,
                  grossAmount: detail.grossAmount,
                  discountPercent: detail.discountPercent,
                  discountAmount: detail.discountAmount,
                  netAmount: detail.netAmount,
                  salesTaxPercent: detail.salesTaxPercent,
                  salesTaxAmount: detail.salesTaxAmount,
                  sroScheduleNo: detail.sroScheduleNo,
                  sroItemSerialNo: detail.sroItemSerialNo,
                  notifiedValue: detail.notifiedValue,
                  furtherTaxPercent: detail.furtherTaxPercent,
                  furtherTaxAmount: detail.furtherTaxAmount,
                  addSalesTaxPercent: detail.addSalesTaxPercent,
                  addSalesTaxAmount: detail.addSalesTaxAmount,
                  valWithoutTax: detail.valWithoutTax,
                  totalTax: detail.totalTax,
                  tax236GValue: detail.tax236GValue,
                  valIncludingTax: detail.valIncludingTax,
                  item: {
                    id: item.id || detail.itemId,
                    name: item.itemName || detail.metaItemName || '',
                    HSCode: item.meta_HSCode || detail.itemHSCode || '',
                    unit: item.meta_unit || detail.unit || '',
                    unitID: item.unitID || detail.uom || '',
                    tradePrice: item.tradePrice || detail.rate || 0,
                    retailPrice: item.retailPrice || detail.notifiedValue || 0,
                    salesTaxPercent: item.salesTaxPercent || detail.salesTaxPercent || 0,
                    addSalesTaxPercent: item.addSalesTaxPercent || detail.addSalesTaxPercent || 0,
                  },
                };
              }),
            };
          })
          .finally(() => {
            loadingInvoice.value = false;
          });
      });

    watch(
      formData,
      (newVal) => {
        console.log('=== WATCH formData from InvoiceEdit', JSON.stringify(newVal, null, 2));
      },
      { deep: true }
    );

    const onSubmit = async () => {
      isLoading.value = true;

      const payload = {
        entity: {
          ...formData.value.entity,
          id: id,
        },
        details: formData.value.details.map((detail) => ({
          ...detail,
          salesInvoiceID: id,
        })),
      };

      store
        .dispatch('sales-invoice/addInvoice', payload)
        .then((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckCircleIcon',
              variant: 'success',
              text: 'Invoice Added Successfully',
            },
          });

          router.push({ name: 'sales-invoice-view', params: { id: response.entity.id } });
        })
        .catch((error) => {
          console.error('Error adding invoice:', error);

          toast({
            component: ToastificationContent,
            props: {
              title: 'Error Adding Invoice',
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: error.message,
            },
          });
        })
        .finally(() => {
          isLoading.value = false;
        });
    };

    const onCancel = () => {
      router.go(-1);
    };

    return {
      loadingInvoice,
      isLoading,
      formData,
      onSubmit,
      onCancel,
    };
  },
};
</script>
